import { useCorporate } from "@/context/CorporateGroupContext";
import { FleetDTO, VehicleQuestion } from "@/interfaces/Company";
import { TableParams } from "@/interfaces/Comun";
import {
  setFleetData,
  setVehicleQuestions,
} from "@/stores/features/vehicleQuestionsSlice";
import { useAppDispatch, useAppSelector } from "@/stores/hooks";
import { companyService } from "@/stores/services/companyService";
import {
  ConfigProvider,
  Input,
  Table,
  TableColumnsType,
  TableProps,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { transformFleetData } from "../../data/data-transform";
import { useFleetData } from "../../hooks/useFleetData";

interface Props {
  companyId: number;
  auditId: number;
}
interface DataType extends VehicleQuestion {
  key: React.Key;
}
/**
 *
 * @param companyId El id de la empresa para temas de consulta
 * @returns
 */
export default function Vehiculos({ companyId, auditId }: Props) {
  const { corporateId } = useCorporate();
  const dispatch = useAppDispatch();

  const { data: flotaVehiculosTerrestres } =
    companyService.useFindAllVehicleQuestionsQuery();

  const { fleetByCompany, isLoadingFleetByCompany } = useFleetData(
    companyId,
    auditId || 0
  );

  const fleetData = useMemo(() => {
    return fleetByCompany ? transformFleetData(fleetByCompany) : [];
  }, [fleetByCompany]);

  const [inputValues, setInputValues] = useState<{
    [key: number]: {
      owned: number;
      contractors: number;
      leasing: number;
      renting: number;
      workers: number;
    };
  }>({});

  const [tableParams, setTableParams] = useState<TableParams<DataType>>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  useEffect(() => {
    if (flotaVehiculosTerrestres) {
      dispatch(setVehicleQuestions(flotaVehiculosTerrestres));
    }
  }, [flotaVehiculosTerrestres, dispatch]);

  useEffect(() => {
    if (!corporateId) {
      dispatch(setFleetData([]));
    }
  }, [corporateId]);

  useEffect(() => {
    if (flotaVehiculosTerrestres) {
      const initialInputValues: {
        [key: number]: {
          owned: number;
          contractors: number;
          leasing: number;
          renting: number;
          workers: number;
        };
      } = {};

      const fleetDataMap = new Map(
        fleetData.map((f) => [f.vehicle_question_id, f])
      );

      const fullFleetData = flotaVehiculosTerrestres.map((q) => {
        const existingData = fleetDataMap.get(q.id);
        const values = {
          owned: existingData?.quantity_owned ?? 0,
          contractors: existingData?.quantity_contractors ?? 0,
          leasing: existingData?.quantity_leasing ?? 0,
          renting: existingData?.quantity_renting ?? 0,
          workers: existingData?.quantity_workers ?? 0,
        };
        initialInputValues[q.id] = values;
        return {
          vehicle_question_id: q.id,
          quantity_owned: values.owned,
          quantity_contractors: values.contractors,
          quantity_leasing: values.leasing,
          quantity_renting: values.renting,
          quantity_workers: values.workers,
        };
      });

      setInputValues(initialInputValues);
      dispatch(setFleetData(fullFleetData));
    }
  }, [flotaVehiculosTerrestres, fleetData, dispatch]);

  const questionsVehicles = useAppSelector(
    (state) => state.vehicleQuestion.questions
  );
  const vehicleQuestion = useAppSelector((state) => state.vehicleQuestion);

  const handleFleetChange = (
    value: number,
    questionId: number,
    type: "owned" | "contractors" | "leasing" | "renting" | "workers"
  ) => {
    const updatedInputValues = {
      ...inputValues,
      [questionId]: {
        ...inputValues[questionId],
        [type]: value,
      },
    };

    setInputValues(updatedInputValues);
    // Crear una copia del estado actual de fleetData
    const updatedFleetData = [...vehicleQuestion.fleetData];

    // Buscar si ya existe un dato para esta pregunta y tipo
    const existingIndex = updatedFleetData.findIndex(
      (data) => data.vehicle_question_id === questionId
    );

    if (existingIndex !== -1) {
      // Si existe, crear un nuevo objeto con las propiedades actualizadas
      const updatedData = {
        ...updatedFleetData[existingIndex], // Copiar todas las propiedades del objeto existente
        vehicle_question_id: questionId, // Asegurarse de que el ID de la pregunta se mantenga
        quantity_owned:
          type === "owned"
            ? value
            : updatedFleetData[existingIndex].quantity_owned,
        quantity_contractors:
          type === "contractors"
            ? value
            : updatedFleetData[existingIndex].quantity_contractors,
        quantity_leasing:
          type === "leasing"
            ? value
            : updatedFleetData[existingIndex].quantity_leasing,
        quantity_renting:
          type === "renting"
            ? value
            : updatedFleetData[existingIndex].quantity_renting,
        quantity_workers:
          type === "workers"
            ? value
            : updatedFleetData[existingIndex].quantity_workers,
      };

      // Reemplazar el objeto existente en el arreglo con el objeto actualizado
      updatedFleetData[existingIndex] = updatedData;
    } else {
      // Si no existe, agregar un nuevo objeto FleetDTO con los valores adecuados
      const newData: FleetDTO = {
        quantity_owned: type === "owned" ? value : 0,
        quantity_contractors: type === "contractors" ? value : 0,
        quantity_leasing: type === "leasing" ? value : 0,
        quantity_renting: type === "renting" ? value : 0,
        vehicle_question_id: questionId,
        quantity_workers: type === "workers" ? value : 0,
        // company: companyId,
      };
      updatedFleetData.push(newData);
    }
    // Actualizar el estado con el nuevo arreglo de fleetData
    dispatch(setFleetData(updatedFleetData));
  };

  const columns: TableColumnsType<DataType> = [
    {
      title: "FLOTA DE VEHICULOS AUTOMOTORES",
      dataIndex: "name",
      width: "350px",
    },
    {
      title: "Cantidad Propios",
      width: "10px",

      render: (_, record) => {
        const fleetInfo = inputValues[record.id] || {
          owned: 0,
          third_party: 0,
          arrended: 0,
          contractors: 0,
          intermediation: 0,
          leasing: 0,
          renting: 0,
        };
        return (
          <>
            <Input
              key={record.id}
              value={fleetInfo.owned ?? 0}
              onChange={(e) => {
                const value = e.target.value;
                handleFleetChange(
                  value === "" ? 0 : parseInt(value, 10) || 0, // Si el valor es "", se asigna 0, si no, se convierte a número.
                  record.id,
                  "owned"
                );
              }}
            />
          </>
        );
      },
    },
    {
      title: "Cantidad Contratistas",
      width: 1,
      render: (_, record) => {
        const fleetInfo = inputValues[record.id] || {
          contractors: 0,
        };
        // //(fleetInfo);
        return (
          <>
            <Input
              key={record.id}
              min={0}
              value={fleetInfo.contractors ?? 0}
              onChange={(e) => {
                const value = e.target.value;
                handleFleetChange(
                  value === "" ? 0 : parseInt(value, 10) || 0,
                  record.id,
                  "contractors"
                );
              }}
            />
          </>
        );
      },
    },
    {
      title: "Cantidad Leasing",
      width: 1,
      render: (_, record) => {
        const fleetInfo = inputValues[record.id] || {
          leasing: 0,
        };
        // //(fleetInfo);
        return (
          <>
            <Input
              key={record.id}
              min={0}
              value={fleetInfo.leasing ?? 0}
              onChange={(e) => {
                const value = e.target.value;
                handleFleetChange(
                  value === "" ? 0 : parseInt(value, 10) || 0,
                  record.id,
                  "leasing"
                );
              }}
            />
          </>
        );
      },
    },
    {
      title: "Cantidad Renting",
      width: 1,
      render: (_, record) => {
        const fleetInfo = inputValues[record.id] || {
          renting: 0,
        };
        // //(fleetInfo);
        return (
          <>
            <Input
              key={record.id}
              min={0}
              value={fleetInfo.renting ?? 0}
              onChange={(e) => {
                const value = e.target.value;
                handleFleetChange(
                  value === "" ? 0 : parseInt(value, 10) || 0,
                  record.id,
                  "renting"
                );
              }}
            />
          </>
        );
      },
    },
    {
      title:
        "Cantidad Vehiculos de los trabajadores puestos al servicio de la organización para cumplir fines misionales.",
      width: "20%",
      render: (_, record) => {
        const fleetInfo = inputValues[record.id] || {
          renting: 0,
        };
        // //(fleetInfo);
        return (
          <>
            <Input
              key={record.id}
              min={0}
              value={fleetInfo.workers ?? 0}
              onChange={(e) => {
                const value = e.target.value;
                handleFleetChange(
                  value === "" ? 0 : parseInt(value, 10) || 0,
                  record.id,
                  "workers"
                );
              }}
            />
          </>
        );
      },
    },
  ];
  const handleTableChange: TableProps<DataType>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const isMultipleSort = Array.isArray(sorter);
    setTableParams({
      pagination,
      filters,
      sortOrder: isMultipleSort ? undefined : sorter.order,
      sortField: isMultipleSort ? undefined : sorter.field,
    });
  };
  const dataSource = questionsVehicles.map((question: VehicleQuestion) => ({
    ...question,
    key: question.id,
  }));
  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: "#e3e3e3",
          },
        },
      }}
    >
      <Table
        columns={columns}
        pagination={tableParams.pagination}
        dataSource={dataSource}
        size="small"
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        showSorterTooltip={{ target: "sorter-icon" }}
        loading={isLoadingFleetByCompany}
        className="w-full"
      />
    </ConfigProvider>
  );
}
